import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ShareFriend from "@/components/ShareFriend";
import { BranchTransferButton } from "@/components/BranchTransferButton";
import DatePickerAU from "@/components/DatePickerAU";
import SavedAddressBar from "@/components/booking/SavedAddressBar";
import BusinessPicker, { businessToAddress } from "@/components/admin/BusinessPicker";
import { enrichWithBusiness } from "@/lib/businessAt";
import ItemsCard from "@/components/booking/ItemsCard";
import QuoteSidebar from "@/components/booking/QuoteSidebar";
import { ScanLabelCard, ScanReviewBanner } from "@/components/booking/ScanCards";
import { toAddr, PICKUP_WINDOWS, minSizeForWeight, sizeIdx, newItem, withUid, isCbrPickup, isCbrDeliveryTown, isCbrDay, CBR_WINDOW, CBR_DAYS, CBR_NOTE, CBR_AREA_MSG, canOverrideArea } from "@/components/booking/constants";
import { Paperclip, FileText, X, Image as ImageIcon } from "lucide-react";
import ShareQuoteDialog from "@/components/ShareQuoteDialog";
import { ATTACH_ACCEPT } from "@/components/OrderAttachments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, AlertTriangle, CalendarClock, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

export default function NewBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onAccount = ["on_account", "paint_shop"].includes(user?.customer_type);
  const isFlorist = user?.customer_type === "florist";
  useEffect(() => { if (isFlorist) navigate("/book-flowers", { replace: true }); }, [isFlorist, navigate]);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [pickupContact, setPickupContact] = useState({ contact_name: "", contact_phone: "", customer_reference: "" });
  const [dropoffContact, setDropoffContact] = useState({ contact_name: "", contact_phone: "", customer_reference: "" });
  const [service, setService] = useState("standard");
  const [urgentAck, setUrgentAck] = useState(false);
  const [items, setItems] = useState([newItem()]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [schedule, setSchedule] = useState({ date: "", window: "" });
  const [creditBalance, setCreditBalance] = useState(0);
  const [instructions, setInstructions] = useState("");
  const applyBusiness = (which, b) => {
    const addr = businessToAddress(b);
    const contact = { contact_name: b.contact_name || "", contact_phone: b.contact_phone || "" };
    if (which === "pickup") { setPickup(addr); setPickupContact((p) => ({ ...p, ...contact })); }
    else { setDropoff(addr); setDropoffContact((p) => ({ ...p, ...contact })); }
    if (b.instructions) setInstructions((cur) => {
      const line = `${which === "pickup" ? "Pickup" : "Delivery"} (${b.trade_name}): ${b.instructions}`;
      return cur.includes(line) ? cur : [cur, line].filter(Boolean).join("\n");
    });
    toast.success(`${b.trade_name} filled in as the ${which === "pickup" ? "pickup" : "drop-off"}`);
  };
  const applyPlace = (which, addr) => {
    const contact = { contact_name: addr.contact_name || "", contact_phone: addr.contact_phone || "" };
    if (which === "pickup") { setPickup(addr); setPickupContact((p) => ({ ...p, ...contact })); }
    else { setDropoff(addr); setDropoffContact((p) => ({ ...p, ...contact })); }
    toast.success(`${addr.business} filled in from Google Maps — check the address`);
  };
  const pickCandidate = (which, c) => {
    (which === "pickup" ? setPickup : setDropoff)((cur) => ({ ...cur, business: c.business }));
    if (c.contact_phone) (which === "pickup" ? setPickupContact : setDropoffContact)((p) => ({ ...p, contact_phone: c.contact_phone }));
  };
  const pickAddress = async (which, addr) => {
    const set = which === "pickup" ? setPickup : setDropoff;
    set(addr);
    if (!canOverrideArea(user)) return;
    const rich = await enrichWithBusiness(addr);
    if (rich === addr) return;
    set(rich);
    const setContact = which === "pickup" ? setPickupContact : setDropoffContact;
    setContact((p) => ({ ...p, contact_name: p.contact_name || rich.contact_name || "", contact_phone: p.contact_phone || rich.contact_phone || "" }));
    if (rich.business_instructions) setInstructions((cur) => {
      const line = `${which === "pickup" ? "Pickup" : "Delivery"} (${rich.business}): ${rich.business_instructions}`;
      return cur.includes(line) ? cur : [cur, line].filter(Boolean).join("\n");
    });
    toast.info(`Looks like ${rich.business} trades at this address`);
  };
  const handoff = new URLSearchParams(location.search);
  const prefill = { pickup: handoff.get("from") || "", dropoff: handoff.get("to") || "" };
  const [atl, setAtl] = useState(true);
  const [atlNote, setAtlNote] = useState("");
  const [atlNoteAuto, setAtlNoteAuto] = useState(null);
  useEffect(() => {
    if (!atl) return;
    let ignore = false;
    api.get("/orders/safe-spot", { params: { line1: dropoff?.line1 || "", city: dropoff?.city || "" } }).then((r) => {
      if (ignore || !r.data.note) return;
      setAtlNote((cur) => (cur && cur !== atlNoteAuto ? cur : r.data.note));
      setAtlNoteAuto(r.data.note);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [dropoff?.line1, dropoff?.city, atl]); // eslint-disable-line react-hooks/exhaustive-deps
  const fileRef = useRef(null);
  const attachRef = useRef(null);
  const [attachFiles, setAttachFiles] = useState([]);
  const quoteTracked = useRef(false);

  useEffect(() => {
    track("booking_opened");
    api.get("/saved-items").then((r) => setSavedItems(r.data)).catch(() => setSavedItems([]));
    api.get("/referrals/me").then((r) => setCreditBalance(r.data.credit_balance || 0)).catch(() => {});
    api.get("/saved-addresses").then((r) => {
      setSavedAddresses(r.data);
      const def = r.data.find((a) => a.is_default_pickup);
      if (def && !location.state?.rebook) {
        setPickup(toAddr(def));
        setPickupContact((p) => ({ ...p, contact_name: def.contact_name || p.contact_name, contact_phone: def.contact_phone || p.contact_phone }));
      }
    }).catch(() => setSavedAddresses([]));
  }, [location.state]);

  useEffect(() => {
    const rb = location.state?.rebook;
    if (!rb) return;
    const mk = (a) => ({ ...a, label: a.label || [a.business, a.line1, a.city, a.postcode].filter(Boolean).join(", ") });
    if (rb.pickup) { setPickup(mk(rb.pickup)); setPickupContact({ contact_name: rb.pickup.contact_name || "", contact_phone: rb.pickup.contact_phone || "", customer_reference: rb.pickup.customer_reference || "" }); }
    if (rb.dropoff) { setDropoff(mk(rb.dropoff)); setDropoffContact({ contact_name: rb.dropoff.contact_name || "", contact_phone: rb.dropoff.contact_phone || "", customer_reference: rb.dropoff.customer_reference || "" }); }
    if (rb.items?.length) setItems(rb.items.map((i) => withUid({ size: i.size || "small", quantity: i.quantity || 1, weight: String(i.weight_kg || 1), desc: i.parcel_description || "", sizeTouched: true })));
    if (rb.service && rb.service !== "florist") setService(rb.service);
    if (rb.scheduled_date) setSchedule((s) => ({ ...s, date: rb.scheduled_date }));
    toast.success(location.state?.transfer ? "Branch transfer loaded — pick the parcels and confirm." : "Loaded your previous delivery — review and confirm.");
  }, [location.state]);

  const saveAddress = async (which) => {
    const a = which === "pickup" ? pickup : dropoff;
    const c = which === "pickup" ? pickupContact : dropoffContact;
    if (!a || a.lat == null) { toast.error("Pick a verified address from the suggestions first"); return; }
    try {
      const { data } = await api.post("/saved-addresses", {
        label: a.business || a.line1 || a.city, business: a.business || "", line1: a.line1 || "", line2: a.line2 || "",
        city: a.city || "", postcode: a.postcode || "", lat: a.lat, lng: a.lng,
        contact_name: c.contact_name || "", contact_phone: c.contact_phone || "",
        which: which === "pickup" ? "sender" : "recipient",
      });
      setSavedAddresses((p) => [data, ...p.filter((x) => x.id !== data.id)]);
      toast.success(which === "pickup" ? "Sender saved" : "Recipient saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save address"); }
  };
  const applySavedAddress = (which, a) => {
    const addr = toAddr(a);
    if (which === "pickup") { setPickup(addr); setPickupContact((p) => ({ ...p, contact_name: a.contact_name || p.contact_name, contact_phone: a.contact_phone || p.contact_phone })); }
    else { setDropoff(addr); setDropoffContact((p) => ({ ...p, contact_name: a.contact_name || p.contact_name, contact_phone: a.contact_phone || p.contact_phone })); }
  };
  const swapAddresses = () => {
    setPickup(dropoff); setDropoff(pickup);
    setPickupContact(dropoffContact); setDropoffContact(pickupContact);
    toast.success("Pickup and drop-off swapped");
  };
  const setDefaultSender = async (id) => {
    try {
      const { data } = await api.put(`/saved-addresses/${id}/default`);
      setSavedAddresses((p) => p.map((x) => ({ ...x, is_default_pickup: x.id === id ? data.is_default_pickup : false })));
      toast.success(data.is_default_pickup ? "Default sender set" : "Default sender cleared");
    } catch { toast.error("Couldn't update default sender"); }
  };
  const deleteSavedAddress = async (id) => {
    try { await api.delete(`/saved-addresses/${id}`); setSavedAddresses((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Couldn't remove address"); }
  };

  const saveItem = async (it) => {
    try {
      const { data } = await api.post("/saved-items", {
        size: it.size, weight_kg: parseFloat(it.weight) || 1, parcel_description: it.desc, label: it.desc,
      });
      setSavedItems((p) => [data, ...p.filter((s) => s.id !== data.id)]);
      toast.success("Saved to your frequent items");
    } catch { toast.error("Couldn't save that item"); }
  };
  const addSavedToItems = (s) => {
    setItems((prev) => {
      const first = prev[0];
      const blank = prev.length === 1 && !first.desc && first.weight === "1" && first.size === "small" && first.quantity === 1;
      const row = withUid({ size: s.size, quantity: 1, weight: String(s.weight_kg), desc: s.parcel_description, sizeTouched: true });
      return blank ? [row] : [...prev, row];
    });
    toast.success(`Added "${s.label}"`);
  };
  const deleteSaved = async (id) => {
    try { await api.delete(`/saved-items/${id}`); setSavedItems((p) => p.filter((s) => s.id !== id)); }
    catch { toast.error("Couldn't remove item"); }
  };

  const scanLabel = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/consignment/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.pickup) {
        setPickup(data.pickup);
        setPickupContact((p) => ({ ...p, contact_name: data.pickup.contact_name || p.contact_name, contact_phone: data.pickup.contact_phone || p.contact_phone }));
      }
      if (data.dropoff) {
        setDropoff(data.dropoff);
        setDropoffContact((p) => ({ ...p, contact_name: data.dropoff.contact_name || p.contact_name, contact_phone: data.dropoff.contact_phone || p.contact_phone }));
      }
      if (data.items?.length) {
        setItems(data.items.map((i) => withUid({
          size: i.size || "small", quantity: i.quantity || 1,
          weight: String(i.weight_kg || 1), desc: i.parcel_description || "", sizeTouched: true,
        })));
      }
      setScanResult({ confidence: data.confidence || "low", warnings: data.warnings || [], pickup: data.pickup, dropoff: data.dropoff });
      toast.success("Label scanned — please review the highlighted details.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read that label");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Size auto-follows the typed weight's bracket; a manual pick can only upgrade.
  const updateItem = (idx, patch) =>
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        if ("size" in patch) next.sizeTouched = true;
        const minS = minSizeForWeight(next.weight);
        if ("weight" in patch && !next.sizeTouched) {
          next.size = minS;
        } else if (sizeIdx(next.size) < sizeIdx(minS)) {
          next.size = minS;
        }
        return next;
      })
    );
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    if (pickup?.lat == null || pickup?.lng == null || dropoff?.lat == null || dropoff?.lng == null) { setQuote(null); return; }
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", {
          pickup, dropoff, service,
          items: items.map((i) => ({ size: i.size, quantity: i.quantity, weight_kg: parseFloat(i.weight) || 1 })),
        });
        setQuote(data);
        if (!quoteTracked.current) { quoteTracked.current = true; track("quote_shown"); }
      } catch { setQuote(null); } finally { setQuoting(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [pickup, dropoff, items, service]);

  const addAttachFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setAttachFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10MB`); continue; }
        if (next.length >= 5) { toast.error("Up to 5 files per consignment"); break; }
        next.push(f);
      }
      return next;
    });
    if (attachRef.current) attachRef.current.value = "";
  };

  const bookAndPay = async () => {
    if (!pickup || !dropoff) { toast.error("Select pickup and dropoff addresses"); return; }
    if (cbrDropoffBlocked) { toast.error(CBR_AREA_MSG); return; }
    setSubmitting(true);
    try {
      const orderRes = await api.post("/orders", {
        pickup: { ...pickup, ...pickupContact },
        dropoff: { ...dropoff, ...dropoffContact },
        service,
        urgent_ack: service === "sameday" && urgentAck,
        items: items.map((i) => ({
          size: i.size, quantity: i.quantity,
          weight_kg: parseFloat(i.weight) || 1, parcel_description: i.desc,
        })),
        scheduled_date: schedule.date,
        pickup_window: schedule.window,
        delivery_instructions: instructions,
        atl,
        atl_note: atl ? atlNote : "",
      });
      for (const f of attachFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/orders/${orderRes.data.id}/attachments`, fd); }
        catch { toast.error(`Couldn't attach ${f.name} — you can add it from the order page.`); }
      }
      const billedToAccount = orderRes.data.billing === "account";
      if (!billedToAccount) {
        const { data } = await api.post("/payments/checkout", {
          order_id: orderRes.data.id, origin_url: window.location.origin,
        });
        if (data.paid_by_credit) {
          track("payment_started", { method: "credit" });
          track("payment_success", { method: "credit" });
          toast.success("Paid in full with your referral credit — no card needed!");
          navigate(`/orders/${orderRes.data.id}`);
          return;
        }
        track("payment_started", { method: "card" });
        window.location.href = data.checkout_url;
        return;
      }
      track("payment_started", { method: "account" });
      track("payment_success", { method: "account" });
      toast.success("Your order has been queued for pickup and has been allocated to your account");
      navigate(`/orders/${orderRes.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally { setSubmitting(false); }
  };

  const addressesVerified = pickup?.lat != null && pickup?.lng != null && dropoff?.lat != null && dropoff?.lng != null;
  const cbr = isCbrPickup(pickup);
  const cbrOutOfArea = cbr && !!dropoff && !isCbrDeliveryTown(dropoff);
  const cbrDropoffBlocked = cbrOutOfArea && !canOverrideArea(user);

  useEffect(() => {
    if (cbr) setSchedule((s) => ({ date: isCbrDay(s.date) ? s.date : "", window: CBR_WINDOW }));
    else setSchedule((s) => (s.window === CBR_WINDOW ? { ...s, window: "" } : s));
  }, [cbr]);

  if (user?.customer_type === "partner") return <Navigate to="/partner" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(user?.customer_type === "paint_shop" ? "/dashboard?courier=1" : "/dashboard")} data-testid="booking-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">New booking</h1>
            <p className="mt-1 text-sm text-muted-foreground">Find addresses by street or business name — pricing updates instantly.</p>
            {onAccount && <BranchTransferButton size="sm" className="mt-3" />}
          </div>
          <ShareFriend />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {user?.customer_type === "paint_shop" && !savedAddresses.some((a) => a.is_default_pickup) && (
              <div className="rounded-lg border border-kilted-gold/50 bg-kilted-gold/10 p-3 text-sm text-kilted-green" data-testid="paint-default-pickup-hint">
                <b>Tip:</b> enter your shop address as the pickup, tap "Save this" under Saved senders, then set it as your default — it will fill in automatically on every courier booking.
              </div>
            )}
            {onAccount && <ScanLabelCard scanning={scanning} fileRef={fileRef} onFileChange={scanLabel} onBulk={() => navigate("/book/bulk")} />}
            <ScanReviewBanner scanResult={scanResult} onDismiss={() => setScanResult(null)} />

            <Card>
              <CardHeader><CardTitle className="text-lg">Pickup</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <AddressAutocomplete label="Pickup address" value={pickup} onSelect={(a) => pickAddress("pickup", a)} testid="pickup-address" initialQuery={prefill.pickup} />
                {canOverrideArea(user) && (
                  <div className="-mt-1 space-y-1">
                    <Label className="text-xs">Business name</Label>
                    <BusinessPicker which="pickup" value={pickup?.business || ""} candidates={pickup?.business_candidates || []} onPick={(b) => applyBusiness("pickup", b)} onPickAddress={(a) => applyPlace("pickup", a)}
                    onPickName={(c) => pickCandidate("pickup", c)} />
                  </div>
                )}
                {scanResult?.pickup?.needs_review && (
                  <p className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700" data-testid="pickup-review-flag">
                    <AlertTriangle className="h-3.5 w-3.5" /> Scanned from photo — please double-check this address.
                  </p>
                )}
                <SavedAddressBar which="pickup" title="Saved senders" list={savedAddresses.filter((a) => a.which !== "recipient")} canSave={pickup?.lat != null}
                  onUse={(a) => applySavedAddress("pickup", a)} onSave={() => saveAddress("pickup")}
                  onSetDefault={setDefaultSender} onDelete={deleteSavedAddress} />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Contact name</Label><Input value={pickupContact.contact_name} onChange={(e) => setPickupContact({ ...pickupContact, contact_name: e.target.value })} data-testid="pickup-contact-name" /></div>
                  <div className="space-y-1.5"><Label>Contact phone</Label><Input value={pickupContact.contact_phone} onChange={(e) => setPickupContact({ ...pickupContact, contact_phone: e.target.value })} data-testid="pickup-contact-phone" /></div>
                </div>
                <div className="space-y-1.5"><Label>Customer reference</Label><Input value={pickupContact.customer_reference} onChange={(e) => setPickupContact({ ...pickupContact, customer_reference: e.target.value })} placeholder="Your reference for this consignment (optional)" data-testid="pickup-customer-reference" /></div>
                <div className="rounded-lg border border-dashed border-border p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><CalendarClock className="h-3.5 w-3.5 text-kilted-gold" /> Schedule pickup {cbr ? "" : "(optional)"}</p>
                  {cbr && (
                    <p className="mb-3 rounded-md border border-kilted-gold/40 bg-kilted-gold/10 px-2.5 py-2 text-xs text-kilted-green" data-testid="cbr-schedule-note">
                      {CBR_NOTE}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Date</Label>
                      <DatePickerAU value={schedule.date} onChange={(d) => setSchedule((s) => ({ ...s, date: d }))} minToday allowedDays={cbr ? CBR_DAYS : undefined} placeholder="Any day" testid="pickup-date-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Time window</Label>
                      {cbr ? (
                        <div className="flex h-9 items-center rounded-md border border-input bg-muted/50 px-3 text-sm" data-testid="pickup-window-locked">{CBR_WINDOW}</div>
                      ) : (
                        <Select value={schedule.window} onValueChange={(v) => setSchedule((s) => ({ ...s, window: v }))}>
                          <SelectTrigger data-testid="pickup-window-select"><SelectValue placeholder="Any time" /></SelectTrigger>
                          <SelectContent>{PICKUP_WINDOWS.map((w) => <SelectItem key={w.v} value={w.v} data-testid={`pickup-window-${w.v}`}>{w.l}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-center -my-3">
              <button type="button" onClick={swapAddresses} data-testid="swap-addresses-button"
                title="Swap pickup and drop-off (e.g. return trip)"
                className="z-10 flex items-center gap-1.5 rounded-full border border-kilted-green/30 bg-white px-3 py-1.5 text-xs font-semibold text-kilted-green shadow-sm transition-colors hover:bg-kilted-green/10">
                <ArrowUpDown className="h-3.5 w-3.5" /> Swap pickup &amp; drop-off
              </button>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-lg">Drop-off</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <AddressAutocomplete label="Drop-off address" value={dropoff} onSelect={(a) => pickAddress("dropoff", a)} testid="dropoff-address" initialQuery={prefill.dropoff} />
                {canOverrideArea(user) && (
                  <div className="-mt-1 space-y-1">
                    <Label className="text-xs">Business name</Label>
                    <BusinessPicker which="dropoff" value={dropoff?.business || ""} candidates={dropoff?.business_candidates || []} onPick={(b) => applyBusiness("dropoff", b)} onPickAddress={(a) => applyPlace("dropoff", a)}
                    onPickName={(c) => pickCandidate("dropoff", c)} />
                  </div>
                )}
                {cbrDropoffBlocked && (
                  <p className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700" data-testid="cbr-dropoff-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {CBR_AREA_MSG}
                  </p>
                )}
                {cbrOutOfArea && !cbrDropoffBlocked && (
                  <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800" data-testid="cbr-override-note">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Drop-off is outside the Canberra-run area — allowed because you're an admin (override).
                  </p>
                )}
                {scanResult?.dropoff?.needs_review && (
                  <p className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700" data-testid="dropoff-review-flag">
                    <AlertTriangle className="h-3.5 w-3.5" /> Scanned from photo — please double-check this address.
                  </p>
                )}
                <SavedAddressBar which="dropoff" title="Saved recipients" list={savedAddresses.filter((a) => a.which !== "sender")} canSave={dropoff?.lat != null}
                  onUse={(a) => applySavedAddress("dropoff", a)} onSave={() => saveAddress("dropoff")}
                  onSetDefault={setDefaultSender} onDelete={deleteSavedAddress} />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Contact name</Label><Input value={dropoffContact.contact_name} onChange={(e) => setDropoffContact({ ...dropoffContact, contact_name: e.target.value })} data-testid="dropoff-contact-name" /></div>
                  <div className="space-y-1.5"><Label>Contact phone</Label><Input value={dropoffContact.contact_phone} onChange={(e) => setDropoffContact({ ...dropoffContact, contact_phone: e.target.value })} data-testid="dropoff-contact-phone" /></div>
                </div>
                <div className="space-y-1.5"><Label>Customer reference</Label><Input value={dropoffContact.customer_reference} onChange={(e) => setDropoffContact({ ...dropoffContact, customer_reference: e.target.value })} placeholder="Your reference for this consignment (optional)" data-testid="dropoff-customer-reference" /></div>
                <div className="space-y-1.5"><Label>Delivery instructions</Label><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Leave at the side door behind the pot plant" data-testid="delivery-instructions-input" /></div>
                <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-kilted-gold/40 bg-kilted-gold/5 p-3 text-sm" data-testid="atl-label">
                  <Checkbox checked={atl} onCheckedChange={(v) => setAtl(!!v)} data-testid="atl-checkbox" className="mt-0.5" />
                  <span><span className="font-semibold text-kilted-green">Authority to Leave (ATL)</span> — the driver may leave the parcel in a safe place if no one's home. Untick to require a signature.</span>
                </label>
                {atl && (
                  <div className="-mt-2 space-y-1 rounded-b-md border border-t-0 border-kilted-gold/40 bg-white px-3 pb-3 pt-2" data-testid="atl-note-box">
                    <Label className="text-xs">Where should we leave it? <span className="font-normal text-muted-foreground">(optional, shown to the driver)</span></Label>
                    <Input value={atlNote} onChange={(e) => setAtlNote(e.target.value)} maxLength={200} placeholder="e.g. Behind the front gate, out of sight from the road" data-testid="atl-note-input" />
                    {atlNoteAuto && atlNote === atlNoteAuto && <p className="text-[11px] text-muted-foreground" data-testid="atl-note-remembered">Remembered from your last ATL booking — edit if it's different this time.</p>}
                  </div>
                )}
                <div className="rounded-lg border border-dashed border-border p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Paperclip className="h-3.5 w-3.5 text-kilted-gold" /> Attach files (optional)</p>
                  {attachFiles.length > 0 && (
                    <div className="mb-2 space-y-1.5">
                      {attachFiles.map((f, i) => (
                        <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-xs" data-testid={`pending-attachment-${i}`}>
                          {f.type === "application/pdf" ? <FileText className="h-3.5 w-3.5 shrink-0 text-kilted-green" /> : <ImageIcon className="h-3.5 w-3.5 shrink-0 text-kilted-green" />}
                          <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
                          <span className="shrink-0 text-muted-foreground">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                          <button type="button" onClick={() => setAttachFiles((prev) => prev.filter((_, x) => x !== i))} data-testid={`pending-attachment-remove-${i}`}
                            className="shrink-0 text-muted-foreground hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input ref={attachRef} type="file" multiple accept={ATTACH_ACCEPT} className="hidden" onChange={addAttachFiles} data-testid="booking-attachment-input" />
                  {attachFiles.length < 5 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => attachRef.current?.click()} data-testid="booking-attach-button"
                      className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                      <Paperclip className="h-3.5 w-3.5" /> Add a file
                    </Button>
                  )}
                  <p className="mt-2 text-[11px] text-muted-foreground">Labels, invoices or photos (JPG/PNG/HEIC/PDF, max 10MB each, up to 5). They're saved with the consignment and included in the proof-of-delivery email.</p>
                </div>
              </CardContent>
            </Card>

            <ItemsCard service={service} onServiceChange={(v) => { setService(v); setUrgentAck(false); }} savedItems={savedItems} items={items}
              updateItem={updateItem} addItem={addItem} removeItem={removeItem} saveItem={saveItem}
              addSavedToItems={addSavedToItems} deleteSaved={deleteSaved} />
          </div>

          <div className="lg:col-span-1">
            <QuoteSidebar addressesVerified={addressesVerified} pickup={pickup} dropoff={dropoff} quoting={quoting}
              quote={quote} creditBalance={creditBalance} onAccount={onAccount} submitting={submitting} onBook={bookAndPay}
              service={service} urgentAck={urgentAck} setUrgentAck={setUrgentAck}
              share={<ShareQuoteDialog pickup={pickup} dropoff={dropoff} items={items} service={service} triggerClassName="w-full" />} />
          </div>
        </div>
      </main>
    </div>
  );
}
